/**
 * AI SDK 6 MCP (Model Context Protocol) Integration
 * 
 * This module provides MCP client functionality for connecting to external
 * tool servers via the standardized Model Context Protocol.
 * 
 * MCP enables AI applications to:
 * - Discover and use tools from external servers
 * - Access resources (files, data) exposed by MCP servers
 * - Use prompts defined by MCP servers
 * 
 * Supported transports:
 * - HTTP (recommended for production)
 * - SSE (Server-Sent Events)
 * - Stdio (for local development only - requires @modelcontextprotocol/sdk)
 * 
 * @see https://modelcontextprotocol.io/
 * @see https://ai-sdk.dev/docs/ai-sdk-core/mcp-tools
 */

import { createMCPClient, type MCPClient } from '@ai-sdk/mcp';
import { z } from 'zod';
import type { Tool } from 'ai';

// =============================================================================
// TYPES
// =============================================================================

export type MCPTransportType = 'http' | 'sse';

export interface MCPHttpTransportConfig {
    type: 'http';
    url: string;
    headers?: Record<string, string>;
}

export interface MCPSSETransportConfig {
    type: 'sse';
    url: string;
    headers?: Record<string, string>;
}

/** Transport configs that can be passed directly to createMCPClient */
export type MCPTransportConfig = MCPHttpTransportConfig | MCPSSETransportConfig;

export interface MCPServerConfig {
    /** Unique identifier for this MCP server */
    id: string;
    /** Display name for the server */
    name: string;
    /** Transport configuration */
    transport: MCPTransportConfig;
    /** Optional description */
    description?: string;
    /** Whether this server is enabled */
    enabled?: boolean;
}

export interface MCPToolSchema {
    inputSchema: z.ZodType<unknown>;
    outputSchema?: z.ZodType<unknown>;
}

export interface MCPClientInstance {
    client: MCPClient;
    config: MCPServerConfig;
    tools: Record<string, Tool>;
}

// =============================================================================
// MCP CLIENT MANAGER
// =============================================================================

/**
 * Manages multiple MCP client connections
 */
export class MCPClientManager {
    private clients: Map<string, MCPClientInstance> = new Map();

    /**
     * Connect to an MCP server
     */
    async connect(config: MCPServerConfig): Promise<MCPClientInstance> {
        // Close existing connection if any
        await this.disconnect(config.id);

        const client = await createMCPClient({
            transport: config.transport,
        });

        // Get tools from the server using schema discovery
        const tools = await client.tools();

        const instance: MCPClientInstance = {
            client,
            config,
            tools,
        };

        this.clients.set(config.id, instance);
        return instance;
    }

    /**
     * Connect to an MCP server with explicit tool schemas for type safety
     */
    async connectWithSchemas(
        config: MCPServerConfig,
        schemas: Record<string, MCPToolSchema>
    ): Promise<MCPClientInstance> {
        await this.disconnect(config.id);

        const client = await createMCPClient({
            transport: config.transport,
        });

        // Get tools with explicit schemas
        const tools = await client.tools({ schemas });

        const instance: MCPClientInstance = {
            client,
            config,
            tools,
        };

        this.clients.set(config.id, instance);
        return instance;
    }

    /**
     * Disconnect from an MCP server
     */
    async disconnect(serverId: string): Promise<void> {
        const instance = this.clients.get(serverId);
        if (instance) {
            await instance.client.close();
            this.clients.delete(serverId);
        }
    }

    /**
     * Disconnect from all MCP servers
     */
    async disconnectAll(): Promise<void> {
        const promises = Array.from(this.clients.keys()).map(id => this.disconnect(id));
        await Promise.all(promises);
    }

    /**
     * Get a connected client instance
     */
    getClient(serverId: string): MCPClientInstance | undefined {
        return this.clients.get(serverId);
    }

    /**
     * Get all tools from all connected MCP servers
     * Prefixes tool names with server ID to avoid conflicts
     */
    getAllTools(): Record<string, Tool> {
        const allTools: Record<string, Tool> = {};

        for (const [serverId, instance] of this.clients) {
            for (const [toolName, tool] of Object.entries(instance.tools)) {
                // Prefix tool name with server ID to avoid conflicts
                allTools[`${serverId}:${toolName}`] = tool;
            }
        }

        return allTools;
    }

    /**
     * Get tools from a specific server
     */
    getServerTools(serverId: string): Record<string, Tool> {
        const instance = this.clients.get(serverId);
        return instance?.tools ?? {};
    }

    /**
     * List all connected servers
     */
    listConnectedServers(): MCPServerConfig[] {
        return Array.from(this.clients.values()).map(instance => instance.config);
    }

    /**
     * Check if a server is connected
     */
    isConnected(serverId: string): boolean {
        return this.clients.has(serverId);
    }
}

// =============================================================================
// SINGLETON INSTANCE
// =============================================================================

let globalMCPManager: MCPClientManager | null = null;

/**
 * Get the global MCP client manager instance
 */
export function getMCPManager(): MCPClientManager {
    if (!globalMCPManager) {
        globalMCPManager = new MCPClientManager();
    }
    return globalMCPManager;
}

// =============================================================================
// HELPER FUNCTIONS
// =============================================================================

/**
 * Create an HTTP transport configuration
 */
export function createHttpTransport(
    url: string,
    headers?: Record<string, string>
): MCPHttpTransportConfig {
    return {
        type: 'http',
        url,
        headers,
    };
}

/**
 * Create an SSE transport configuration
 */
export function createSSETransport(
    url: string,
    headers?: Record<string, string>
): MCPSSETransportConfig {
    return {
        type: 'sse',
        url,
        headers,
    };
}

/**
 * Quick connect to a single MCP server and get its tools
 * 
 * @example
 * const tools = await connectMCPServer({
 *   id: 'my-server',
 *   name: 'My MCP Server',
 *   transport: createHttpTransport('https://mcp.example.com/api'),
 * });
 * 
 * // Use with aiSDKChatHandler
 * await aiSDKChatHandler(apiKeys, 'gpt-4o', messages, signal, {
 *   tools,
 * });
 */
export async function connectMCPServer(config: MCPServerConfig): Promise<Record<string, Tool>> {
    const manager = getMCPManager();
    const instance = await manager.connect(config);
    return instance.tools;
}

/**
 * Connect to multiple MCP servers and get all their tools combined
 * 
 * @example
 * const tools = await connectMCPServers([
 *   {
 *     id: 'github',
 *     name: 'GitHub MCP Server',
 *     transport: createHttpTransport('https://mcp-github.example.com'),
 *   },
 *   {
 *     id: 'search',
 *     name: 'Search MCP Server',
 *     transport: createSSETransport('https://mcp-search.example.com/sse'),
 *   },
 * ]);
 */
export async function connectMCPServers(configs: MCPServerConfig[]): Promise<Record<string, Tool>> {
    const manager = getMCPManager();

    await Promise.all(configs.map(config => manager.connect(config)));

    return manager.getAllTools();
}

/**
 * Disconnect all MCP servers
 */
export async function disconnectAllMCPServers(): Promise<void> {
    const manager = getMCPManager();
    await manager.disconnectAll();
}

// =============================================================================
// INTEGRATION WITH AI SDK
// =============================================================================

/**
 * Use MCP tools with AI SDK chat handlers
 * 
 * This is a higher-order function that wraps the AI SDK handler
 * to automatically include MCP tools.
 * 
 * @example
 * import { aiSDKChatHandler } from './ai-sdk';
 * import { withMCPTools, createHttpTransport } from './ai-mcp';
 * 
 * const mcpServers = [
 *   {
 *     id: 'my-server',
 *     name: 'My MCP Server',
 *     transport: createHttpTransport('https://mcp.example.com'),
 *   },
 * ];
 * 
 * const handler = await withMCPTools(mcpServers);
 * 
 * // handler.tools contains all MCP tools
 * const result = await aiSDKChatHandler(apiKeys, 'gpt-4o', messages, signal, {
 *   tools: handler.tools,
 * });
 * 
 * // Don't forget to cleanup when done
 * await handler.cleanup();
 */
export async function withMCPTools(servers: MCPServerConfig[]): Promise<{
    tools: Record<string, Tool>;
    cleanup: () => Promise<void>;
}> {
    const tools = await connectMCPServers(servers);

    return {
        tools,
        cleanup: () => disconnectAllMCPServers(),
    };
}
